<template>
    <div class="container-fluid" :class="{closed: isClosed}" id="plot">
        <nav>
            <ol class="breadcrumb">
                <li class="breadcrumb-item"><router-link to="/mypage">マイページ</router-link></li>
                <li class="breadcrumb-item"><router-link :to="booksPath">小説管理</router-link></li>
                <li class="breadcrumb-item"><router-link :to="`/mypage/book/${$route.params.book_id}/sections`">本文一覧</router-link></li>
                <li class="breadcrumb-item">プロット</li>
            </ol>
        </nav>
        <legend class="text-center my-4">
            <h3 v-if="book.id" class="mincho" @dblclick.meta="isDebug = true" v-html="$filters.ruby(book.title + ' のプロット')"></h3>
            <h4 class="subtitle mincho" v-if="book.subtitle">{{book.subtitle}}</h4>
        </legend>
        <div class="container">
            <div v-if="isSorted" class="my-2">
                <button class="btn btn-outline-success btn-sm" @click="updateStatus()">
                    <i class="fas fa-check"></i>
                    順番更新
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="load()">
                    <i class="fas fa-times-circle"></i>
                    キャンセル
                </button>
            </div>
            <div class="instruction" v-if="!isLoading && !hasSavedSections">
                <i class="fas fa-ruler big-icon"></i>
                <div class="description">
                    長編小説を最後まで執筆するには、最初に全てのエピソードを作ることが近道です。
                    タイトルだけの空の本文を追加して、140字以内のプロットを添えることができます。<br>
                    右のノートで全体の梗概を作成したら、以下の「本文追加」からエピソードを追加してください。
                    追加したエピソードは、後から分解したり結合することができます。
                </div>
            </div>
            <plot-card v-for="s in sections" :key="s.id"
                :section="s"
                :isSorted="isSorted"
                :hasNew="hasNew"
                @reload="load($event)"
                @debug="debug($event)"
                @moveSection="moveSection($event)"
                @remove="remove($event)"></plot-card>
            <div v-if="isSorted" class="my-2">
                <button class="btn btn-outline-success btn-sm" @click="updateStatus()">
                    <i class="fas fa-check"></i>
                    順番更新
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="load()">
                    <i class="fas fa-times-circle"></i>
                    キャンセル
                </button>
            </div>
            <div class="d-flex justify-content-center mb-5" v-if="!isLoading && !hasNew">
                <button class="btn btn-outline-primary mr-3" @click="addSection()" :disabled="isSorted">
                    <i class="fas fa-plus"></i>
                    本文追加
                </button>
                <button class="btn btn-outline-secondary" @click="addChapter()" :disabled="isSorted">
                    <i class="fas fa-folder-open"></i>
                    章追加
                </button>
            </div>
            <div class="text-center mb-5" v-if="!isLoading">
                <router-link :to="`/mypage/book/${$route.params.book_id}/sections`">
                    <i class="fas fa-list"></i>
                    本文一覧へ
                </router-link>
            </div>
        </div>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
        <note
            ref="note"
            :book_id="$route.params.book_id"
            :order="1"
            @toggle="toggle($event, 'note')"
            @searchNote="searchNote"></note>
        <planner
            ref="planner"
            :book_id="$route.params.book_id"
            :order="2"
            @toggle="toggle($event, 'planner')"></planner>
        <search-note :book_id="$route.params.book_id" @found="foundNote($event)" @replace="replaceNote($event)"></search-note>
        <section-move
            :section="section"
            :sections="sections"
            @moveTo="moveTo($event)"></section-move>
        <debug-modal
            :section_id="section_id"
            @saved="load()"></debug-modal>
    </div>
</template>

<style scoped lang="scss">
#plot {
    padding-right: 640px;
    transition: 0.2s ease;
    &.closed {
        padding-right: 15px;
    }
}
.instruction {
    text-align: center;
    .big-icon {
        font-size: 12rem;
        color: #aaa;
    }
    .description {
        max-width: 640px;
        margin: 3rem auto;
        padding: 1rem;
        background-color: #4c834c;
        border-radius: .5rem;
        color: snow;
        text-align: left;
        position: relative;
        &:before {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -15px;
            border: 15px solid transparent;
            border-top: 15px solid #4c834c;
        }
    }
}
.container {
    max-width: 960px;
    padding: 0;
}
@media screen and (max-width: 1340px) {
    #plot {
        padding-right: 15px;
    }
}
</style>

<script>
import note from "../sections/sidebars/note";
import planner from "../sections/sidebars/planner";
import searchNote from "../notes/searchNote";
import debugModal from "./debugModal";
import sectionMove from "../sections/modals/section-move";
import plotCard from "./plotCard";
export default {
    components: {
        note,
        planner,
        searchNote,
        debugModal,
        sectionMove,
        plotCard,
    },
    data() {
        return {
            book: {},
            sections: [],
            isClosed: true,
            isLoading: false,
            section_id: null,
            chapter_id: null,
            section: null,
            newSection: null,
            isDebug: false,
        }
    },
    methods: {
        load(id = null) {
            this.isLoading = true;
            axios.get("/mypage/book/sections/get", {params: {id: this.$route.params.book_id}}).then(res => {
                let order = 1
                for (const d of res.data) {
                    if (!d.is_chapter) {
                        d.order = order++;
                    }
                }
                const newOne = this.hasNew;
                if (newOne && id) {
                    res.data.push(newOne);
                }
                this.sections = res.data;
                this.isLoading = false;
                this.$refs.planner.load();
            }).catch(err => {
                alert.err(err);
                this.isLoading = false;
            });
        },
        loadBook() {
            axios.get("/mypage/book/get", {params: {id: this.$route.params.book_id}}).then(res => {
                this.book = res.data;
                if (!this.book.is_long) {
                    alert.err("短編のプロットは作成できません。");
                    this.$router.push("/mypage/book/" + this.$route.params.book_id + "/sections");
                }
            }).catch(err => {
                alert.err(err);
            });
        },
        toggle(is_closed, ref) {
            this.isClosed = is_closed;
            const refs = [
                "note",
                "planner",
            ];
            for (let r of refs) {
                if (r !== ref && this.$refs[r]) {
                    this.$refs[r].toggle(is_closed);
                }
            }
        },
        addSection() {
            this.sections.push({
                "id": null,
                "is_chapter": false,
                "book_id": parseInt(this.$route.params.book_id),
                "section_name": null,
                "plot": null,
                "characters": 0,
                "order_number": this.sections.length + 1,
            });
        },
        addChapter() {
            this.sections.push({
                "id": null,
                "is_chapter": true,
                "book_id": parseInt(this.$route.params.book_id),
                "section_name": null,
                "plot": null,
                "characters": 0,
                "order_number": this.sections.length + 1,
            });
        },
        remove(v) {
            if (v.is_chapter) {
                Swal.fire({
                    title: "章削除",
                    html: `章を削除します。<br>
                    章を削除しても、本文が消えることはありません。`,
                    icon: "warning",
                    showCancelButton: true,
                    cancelButtonText: "キャンセル",
                    confirmButtonText: "章削除",
                    confirmButtonColor: "crimson",
                    reverseButtons: true,
                }).then(confirm => {
                    if (confirm.isConfirmed) {
                        axios.post("/mypage/chapter/remove", {id: v.id}).then(res => {
                            alert.msg(res.data);
                            this.load();
                        }).catch(err => {
                            alert.err(err.response);
                        });
                    }
                });
            } else {
                Swal.fire({
                    title: "本文の削除",
                    html: `<div class="text-left">
                        指定の本文<br/>
                        <b>${v.section_name}</b><br/>
                        を削除します。<br/>
                        話は即座に削除され、もとに戻せません。<br/>
                        よろしいですか？</div>
                    `,
                    icon: "error",
                    showCancelButton: true,
                    confirmButtonColor: "crimson",
                    cancelButtonColor: "gray",
                    confirmButtonText: "削除",
                    cancelButtonText: "キャンセル",
                    reverseButtons: true,
                }).then(confirm => {
                    if (confirm.isConfirmed) {
                        this.isLoading = true;
                        axios.post("/mypage/book/section/remove", {id: v.id}).then(res => {
                            alert.msg(res.data);
                            this.load();
                        }).catch(err => {
                            alert.err(err.response);
                        }).finally(() => {
                            this.isLoading = false;
                        });
                    }
                });
            }
        },
        moveSection(v) {
            this.section = v;
            setTimeout(() => {$("#move-modal").modal()});
        },
        moveTo(to_id) {
            const from = _.findIndex(this.sections, {id: this.section.id});
            const dest = _.findIndex(this.sections, {id: to_id});   // from抜く前
            this.sections.splice(from, 1);
            const to = _.findIndex(this.sections, {id: to_id});     // from抜いた後
            if (from < dest) {
                // 下がる
                this.sections.splice(to + 1, 0, this.section);
            } else {
                // 上がる
                this.sections.splice(to, 0, this.section);
            }
        },
        updateStatus() {
            this.isLoading = true;
            axios.post("/mypage/book/sections/update", {
                "sections": this.sections,
            }).then(res => {
                alert.msg(res.data);
                this.load();
            }).catch(err => {
                alert.err(err.response);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        searchNote() {
            $("#search-note").modal();
        },
        foundNote(result) {
            this.$refs.note.$refs.notes.found(result);
        },
        replaceNote(words) {
            this.$refs.note.$refs.notes.replaceKeyword(words);
        },
        debug(s) {
            this.section_id = s.id;
            setTimeout(() => {
                $("#debug-modal").modal();
            });
        }
    },
    computed: {
        booksPath() {
            if (this.book?.is_hidden === 3) {
                return "/mypage/books/pending";
            } else if (this.book?.property?.is_finished) {
                return "/mypage/books/finished";
            }
            return "/mypage/books/writing";
        },
        // 並び替え中
        isSorted() {
            let order_number = 1;
            for (let i = 0; i < this.sections.length; i++) {
                if (this.sections[i].order_number != order_number) {
                    return true;
                }
                order_number++;
            }
            return false;
        },
        hasNew() {
            return _.find(this.sections, (o) => { return (!o.section_name && !o.plot); });
        },
        hasSavedSections() {
            return _.find(this.sections, "id");
        }
    },
    mounted() {
        this.loadBook();
        this.load();
    }
}
</script>